/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */



import React, { useCallback, useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import {
  Card,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Tooltip,
  Chip,
  Box,
  Divider,
  Snackbar,
} from "@mui/material";

import {

  ArrowBack,

  ArrowForward,

  ContentCopy,

  Visibility,

  VisibilityOff,

  CheckCircle,

  OpenInNew,

  VpnKey,

  Webhook,

  PowerSettingsNew,

  InfoOutlined,

} from "@mui/icons-material";

import MDBox from "components/MDBox";

import MDTypography from "components/MDTypography";

import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from "auth/Auth";

import razorpayLogo from "assets/stripeandrazorpaylogo/Razorpay_logo.webp";
import { useTheme } from "@mui/material/styles";

// PearlCRM theme info blue (not Stripe purple)
const THEME_BLUE = "#1565C8";
const THEME_BLUE_HOVER = "#0D47A1";
const RAZORPAY_BLUE = "#0C2452";

const EXPIRY_MIN_HOURS = 1;
const EXPIRY_MAX_HOURS = 720;
const EXPIRY_FALLBACK_DEFAULT_HOURS = 336;

const formatHours = (h) => {
  const n = Number(h);
  if (!Number.isFinite(n) || n < 1) return "";
  if (n < 24) return `${n} hour${n === 1 ? "" : "s"}`;
  const d = Math.floor(n / 24);
  const r = n % 24;
  const dayPart = `${d} day${d === 1 ? "" : "s"}`;
  return r ? `${dayPart} ${r} hour${r === 1 ? "" : "s"}` : dayPart;
};

const GATEWAY_META = {
  stripe: {
    title: "Stripe",
    logo: null,
    brandColor: THEME_BLUE,
    brandHover: THEME_BLUE_HOVER,
    dashboardUrl: "https://dashboard.stripe.com/apikeys",
    webhookUrl: "https://dashboard.stripe.com/webhooks",
    description:
      "Connect your own Stripe account. Payments go directly to your account — PearlCRM never uses a shared platform key.",
    keyLabel: "Publishable key",
    secretLabel: "Secret key",
    keyPlaceholder: "pk_live_… or pk_test_…",
    secretPlaceholder: "sk_live_… or sk_test_…",
    webhookEvents: "checkout.session.completed, payment_intent.succeeded, payment_intent.payment_failed, charge.refunded",
    webhookSteps: [
      "Open Stripe Dashboard → Developers → Webhooks → Add destination (or Add endpoint).",
      "When Stripe asks which account to listen to, select Your account. Do not select Connected accounts — PearlCRM uses your own Stripe keys, not Stripe Connect.",
      "Click Continue, then choose Events on your account.",
      "Select these events: checkout.session.completed, payment_intent.succeeded, and payment_intent.payment_failed.",
      "Choose Webhook endpoint as the destination type and click Continue.",
      "Paste the Webhook URL from PearlCRM (below) into the Endpoint URL field.",
      "Click Create destination / Add endpoint to save.",
      "Open the new webhook, click Reveal under Signing secret, and copy the whsec_… value.",
      "Paste that signing secret into PearlCRM below, then complete step 4 and Save connection.",
    ],

  },

  razorpay: {

    title: "Razorpay",

    logo: razorpayLogo,

    brandColor: RAZORPAY_BLUE,

    brandHover: "#2B84EA",

    dashboardUrl: "https://dashboard.razorpay.com/app/keys",

    webhookUrl: "https://dashboard.razorpay.com/app/webhooks",

    description:

      "Connect your own Razorpay account. Keys and webhooks are configured by you in the Razorpay dashboard.",

    keyLabel: "Key ID",

    secretLabel: "Key secret",

    keyPlaceholder: "rzp_live_… or rzp_test_…",

    secretPlaceholder: "Your Razorpay key secret",

    webhookEvents: "payment.captured, payment.failed",
    webhookSteps: [
      "Open Razorpay Dashboard → Settings → Webhooks → Add New Webhook.",
      "Paste the Webhook URL from PearlCRM (below) into the Webhook URL field.",
      "Enable events: payment.captured and payment.failed.",
      "Save the webhook and copy the Webhook Secret shown by Razorpay.",
      "Paste that secret into PearlCRM below, then complete step 4 and Save connection.",
    ],

  },

};



const STEPS = [

  { id: "keys-info", label: "API keys", icon: InfoOutlined },

  { id: "keys-enter", label: "Enter keys", icon: VpnKey },

  { id: "webhook", label: "Webhook", icon: Webhook },

  { id: "enable", label: "Enable", icon: PowerSettingsNew },

];



function StepProgress({ steps, activeStep, brandColor, onStepClick, completedThrough }) {

  return (

    <MDBox
      display="flex"
      flexWrap="wrap"
      gap={1}
      mb={3}
      pb={2}
      borderBottom="1px solid"
      borderColor="divider"
    >

      {steps.map((step, index) => {

        const Icon = step.icon;

        const isActive = index === activeStep;

        const isDone = index < completedThrough;

        const canClick = index <= completedThrough;



        return (

          <MDBox
            key={step.id}
            onClick={canClick ? () => onStepClick(index) : undefined}
            sx={{

              display: "flex",

              alignItems: "center",

              gap: 1,

              px: 1.5,

              py: 1,

              borderRadius: 2,

              border: "1px solid",

              borderColor: isActive ? brandColor : isDone ? "success.light" : "divider",

              bgcolor: isActive ? `${brandColor}12` : isDone ? "success.50" : "background.paper",

              cursor: canClick ? "pointer" : "default",

              opacity: canClick ? 1 : 0.55,

              transition: "all 0.15s ease",

              "&:hover": canClick

                ? { borderColor: brandColor, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }

                : {},

            }}
          >

            <Box
              sx={{

                width: 28,

                height: 28,

                borderRadius: "50%",

                display: "flex",

                alignItems: "center",

                justifyContent: "center",

                bgcolor: isActive ? brandColor : isDone ? "success.main" : "grey.200",

                color: isActive || isDone ? "#fff" : "text.secondary",

                fontSize: 13,

                fontWeight: 700,

              }}
            >

              {isDone ? <CheckCircle sx={{ fontSize: 16 }} /> : <Icon sx={{ fontSize: 16 }} />}

            </Box>

            <MDTypography

              variant="button"
              fontWeight={isActive ? "bold" : "regular"}
              color={isActive ? "dark" : "text"}
              sx={{ whiteSpace: "nowrap" }}
            >

              {index + 1}. {step.label}

            </MDTypography>

          </MDBox>

        );

      })}

    </MDBox>

  );

}



function InstructionCard({ title, children, action }) {
  return (
    <MDBox
      sx={{
        p: 2.5,
        mb: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "#FAFBFC",
      }}
    >
      <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
        {title}
      </MDTypography>
      <MDTypography variant="body2" color="text" sx={{ lineHeight: 1.7 }}>
        {children}
      </MDTypography>
      {action && <MDBox mt={2}>{action}</MDBox>}
    </MDBox>
  );
}

function NumberedSteps({ steps, brandColor }) {
  return (
    <MDBox
      component="ol"
      sx={{
        m: 0,
        pl: 2.5,
        "& li": { mb: 1.25, lineHeight: 1.65, color: "text.secondary" },
        "& li::marker": { color: brandColor, fontWeight: 700 },
      }}
    >
      {steps.map((step) => (
        <MDTypography key={step} component="li" variant="body2" color="text">
          {step}
        </MDTypography>
      ))}
    </MDBox>
  );
}



export default function PaymentGatewaySetupPage() {
  const isV2 = useTheme()?.pearlUi === "v2";

  const { gateway } = useParams();

  const navigate = useNavigate();

  const { axiosInstance } = useAuth();

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");

  const [success, setSuccess] = useState("");

  const [activeStep, setActiveStep] = useState(0);

  const [showSecret, setShowSecret] = useState(false);

  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  const [enabled, setEnabled] = useState(false);

  const [publicKey, setPublicKey] = useState("");

  const [secretKey, setSecretKey] = useState("");

  const [webhookSecret, setWebhookSecret] = useState("");

  const [secretMasked, setSecretMasked] = useState(null);

  const [webhookMasked, setWebhookMasked] = useState(null);

  const [secretEdited, setSecretEdited] = useState(false);

  const [webhookEdited, setWebhookEdited] = useState(false);

  const [webhookUrl, setWebhookUrl] = useState("");

  const [hasWebhookSecret, setHasWebhookSecret] = useState(false);

  const [configured, setConfigured] = useState(false);

  const [expiryHours, setExpiryHours] = useState("");
  const [expiryDefault, setExpiryDefault] = useState(EXPIRY_FALLBACK_DEFAULT_HOURS);
  const [expiryEdited, setExpiryEdited] = useState(false);
  const [expiryReset, setExpiryReset] = useState(false);

  const expiryNumber = Number(expiryHours);
  const expiryValid =
      /^\d+$/.test(String(expiryHours).trim()) &&
      expiryNumber >= EXPIRY_MIN_HOURS &&
      expiryNumber <= EXPIRY_MAX_HOURS;

  const meta = GATEWAY_META[gateway] || GATEWAY_META.stripe;

  const isStripe = gateway === "stripe";

  // Treat saved (masked) secrets as present so re-open / Next step work without re-typing.
  const keysReady = Boolean(
    publicKey.trim() &&
      (secretEdited
        ? Boolean(secretKey.trim()) && !String(secretKey).includes("•")
        : Boolean(secretKey.trim() || secretMasked || configured))
  );

  const webhookReady = Boolean(
    webhookEdited
      ? Boolean(webhookSecret.trim()) && !String(webhookSecret).includes("•")
      : Boolean(webhookSecret.trim() || hasWebhookSecret || webhookMasked)
  );

  const completedThrough = (() => {

    if (keysReady && webhookReady) return 3;

    if (keysReady) return 2;

    if (publicKey.trim()) return 1;

    return 0;

  })();

  const applyProvider = useCallback(
    (provider) => {
      const secretMask = isStripe ? provider?.secret_key_masked : provider?.key_secret_masked;
      const whMask = provider?.webhook_secret_masked;
      const isConfigured = Boolean(provider?.configured);
      const hasSecret = Boolean(provider?.has_secret_key || secretMask || isConfigured);
      const hasWebhook = Boolean(provider?.has_webhook_secret || whMask);

      setEnabled(Boolean(provider?.enabled));
      setConfigured(isConfigured);
      setPublicKey(isStripe ? provider?.publishable_key || "" : provider?.key_id || "");
      setSecretMasked(secretMask || null);
      setWebhookMasked(whMask || null);
      // Always show saved secrets in the inputs (masked). Never leave blank when configured.
      setSecretKey(secretMask || (hasSecret ? "••••••••••••••••" : ""));
      setWebhookSecret(whMask || (hasWebhook ? "••••••••••••••••" : ""));
      setSecretEdited(false);
      setWebhookEdited(false);
      setWebhookUrl(provider?.webhook_url || "");
      setHasWebhookSecret(hasWebhook);
      setShowSecret(false);
      setShowWebhookSecret(false);
    },
    [isStripe]
  );

  const applyExpiry = useCallback((data) => {
    const def = Number(data?.payment_link_expiry_hours_default) || EXPIRY_FALLBACK_DEFAULT_HOURS;
    const stored = data?.payment_link_expiry_hours;
    setExpiryDefault(def);
    setExpiryHours(String(stored ?? def));
    setExpiryEdited(false);
    setExpiryReset(false);
  }, []);

  const load = useCallback(async () => {

    setLoading(true);

    setError("");

    try {

      const res = await axiosInstance.get("/payment-gateways/settings");

      const provider = isStripe ? res.data?.stripe : res.data?.razorpay;

      applyProvider(provider);
      applyExpiry(res.data);

      if (provider?.configured) {
        setActiveStep(3);
      }

    } catch (err) {

      setError(err?.response?.data?.detail || err?.message || "Unable to load settings.");

    } finally {

      setLoading(false);

    }

  }, [axiosInstance, isStripe, applyProvider, applyExpiry]);

  useEffect(() => {

    if (gateway === "razorpay") {

      navigate("/settings/payments", { replace: true });

      return;

    }

    if (!GATEWAY_META[gateway]) {

      navigate("/settings/payments", { replace: true });

      return;

    }

    load();

  }, [gateway, navigate, load]);

  // First-time setup: turn Enable on when the user reaches the last step with keys ready.
  useEffect(() => {
    if (activeStep === 3 && keysReady && !configured) {
      setEnabled(true);
    }
  }, [activeStep, keysReady, configured]);

  if (gateway === "razorpay") {

    return null;

  }

  const copyWebhook = async () => {

    try {

      await navigator.clipboard.writeText(webhookUrl);

      setSuccess("Webhook URL copied.");

    } catch {

      setError("Could not copy webhook URL.");

    }

  };



  const handleSave = async () => {

    if (isStripe && expiryEdited && !expiryReset && !expiryValid) {
      setError(`Link expiry must be a whole number between ${EXPIRY_MIN_HOURS} and ${EXPIRY_MAX_HOURS} hours.`);
      return;
    }

    setSaving(true);

    setError("");

    setSuccess("");

    try {

      const endpoint = isStripe

        ? "/payment-gateways/settings/stripe"

        : "/payment-gateways/settings/razorpay";

      // Only send secrets when the user typed a new value (never wipe saved secrets).
      const nextSecret =
        secretEdited && secretKey.trim() && !String(secretKey).includes("•")
          ? secretKey.trim()
          : undefined;

      const nextWebhook =
        webhookEdited &&
        webhookSecret.trim() &&
        !String(webhookSecret).includes("•")
          ? webhookSecret.trim()
          : undefined;

      // Persist enable flag from the step-4 switch (auto-on for first-time setup).
      const payload = isStripe

        ? {

            enabled,

            publishable_key: publicKey.trim() || undefined,

            ...(nextSecret ? { secret_key: nextSecret } : {}),

            ...(nextWebhook ? { webhook_secret: nextWebhook } : {}),

            ...(expiryReset ? { clear_payment_link_expiry: true } : {}),
            ...(expiryEdited && !expiryReset && expiryValid
                ? { payment_link_expiry_hours: expiryNumber }
                : {}),

          }

        : {

            enabled,

            key_id: publicKey.trim() || undefined,

            ...(nextSecret ? { key_secret: nextSecret } : {}),

            ...(nextWebhook ? { webhook_secret: nextWebhook } : {}),

          };

      const res = await axiosInstance.put(endpoint, payload);

      const provider = isStripe ? res.data?.stripe : res.data?.razorpay;

      applyProvider(provider);
      applyExpiry(res.data);

      setSuccess(
        provider?.enabled
          ? `${meta.title} is connected.`
          : `${meta.title} settings saved.`
      );

      // Return to Stripe / Razorpay cards after a successful save.
      window.setTimeout(() => {
        navigate("/settings/payments", {
          replace: true,
          state: {
            paymentGatewayMessage: provider?.enabled
              ? `${meta.title} is connected.`
              : `${meta.title} settings saved.`,
          },
        });
      }, 600);

    } catch (err) {

      setError(

        err?.response?.data?.detail || err?.message || `Unable to save ${meta.title} settings.`

      );

    } finally {

      setSaving(false);

    }

  };



  const goNext = () => {

    if (activeStep === 1 && !keysReady) {

      setError("Enter both API keys before continuing.");

      return;

    }

    setError("");

    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));

  };



  const goBack = () => {

    setError("");

    setActiveStep((s) => Math.max(s - 1, 0));

  };



  const renderStepContent = () => {

    switch (activeStep) {

      case 0:

        return (

          <>

            <MDTypography variant="h6" fontWeight="medium" mb={2}>

              Step 1 — Get your API keys

            </MDTypography>

            <InstructionCard title={`Where to find keys in ${meta.title}`}>

              {isStripe ? (

                <>

                  Log in to your Stripe dashboard, open <strong>Developers → API keys</strong>, and

                  copy your <strong>Publishable key</strong> and <strong>Secret key</strong>.

                  Use test keys (<code>pk_test_</code> / <code>sk_test_</code>) while setting up,

                  then switch to live keys when you go live.

                </>

              ) : (

                <>

                  Log in to Razorpay, go to <strong>Settings → API Keys</strong>, and copy your{" "}

                  <strong>Key ID</strong> and <strong>Key Secret</strong>.

                </>

              )}

              <MDButton
                component="a"
                href={meta.dashboardUrl}
                target="_blank"
                rel="noopener noreferrer"

                variant="outlined"
                size="small"
                startIcon={<OpenInNew />}
                sx={{ mt: 2, borderColor: meta.brandColor, color: meta.brandColor }}
              >

                Open {meta.title} dashboard

              </MDButton>

            </InstructionCard>

            <Alert severity="info" icon={<InfoOutlined />}>

              PearlCRM stores keys per tenant only. Payments are collected in your {meta.title}{" "}

              account, not a shared PearlCRM account.

            </Alert>

          </>

        );



      case 1:

        return (

          <>

            <MDTypography variant="h6" fontWeight="medium" mb={2}>

              Step 2 — Enter your keys

            </MDTypography>

            <InstructionCard title="Paste keys from your dashboard">

              Enter the keys you copied in step 1. After saving, reopen this page anytime — your

              publishable key and masked secrets stay filled in. Only type a new secret when you

              want to replace it.

            </InstructionCard>

            {(secretMasked || configured) && (
              <Alert severity="success" sx={{ mb: 2, maxWidth: 720 }} icon={<CheckCircle />}>
                Secret key is saved
                {secretMasked ? ` (${secretMasked})` : ""}. Leave it as-is, or focus the field to
                replace it.
              </Alert>
            )}

            <MDBox maxWidth={720}>

              <TextField

                fullWidth
                label={meta.keyLabel}
                value={publicKey}
                onChange={(e) => setPublicKey(e.target.value)}
                placeholder={meta.keyPlaceholder}
                margin="normal"
                autoComplete="off"
                sx={{
                  bgcolor: "#fff",
                  "& .MuiInputBase-input": {
                    color: "#344767",
                    WebkitTextFillColor: "#344767",
                    opacity: 1,
                  },
                }}
              />

              <TextField

                fullWidth
                label={meta.secretLabel}
                type={showSecret ? "text" : "password"}
                value={secretKey}
                onChange={(e) => {
                  setSecretEdited(true);
                  setSecretKey(e.target.value);
                }}
                onFocus={() => {
                  if (!secretEdited && (secretMasked || configured)) {
                    setSecretEdited(true);
                    setSecretKey("");
                    setShowSecret(true);
                  }
                }}
                placeholder={
                  secretMasked || configured
                    ? "Saved — type a new key only to replace"
                    : meta.secretPlaceholder
                }
                helperText={
                  !secretEdited && (secretMasked || configured)
                    ? `Saved secret on file${secretMasked ? `: ${secretMasked}` : ""}. Focus the field to replace it.`
                    : undefined
                }
                margin="normal"
                autoComplete="new-password"
                sx={{
                  bgcolor: "#fff",
                  "& .MuiInputBase-input": {
                    color: "#344767",
                    WebkitTextFillColor: "#344767",
                    opacity: 1,
                  },
                }}
                InputProps={{

                  endAdornment: (

                    <InputAdornment position="end">

                      <IconButton onClick={() => setShowSecret((v) => !v)} edge="end" size="small">

                        {showSecret ? <VisibilityOff /> : <Visibility />}

                      </IconButton>

                    </InputAdornment>

                  ),

                }}
              />

            </MDBox>

          </>

        );



      case 2:

        return (

          <>

            <MDTypography variant="h6" fontWeight="medium" mb={2}>

              Step 3 — Configure webhook

            </MDTypography>

            <InstructionCard title="Why you need a webhook">

              Webhooks let PearlCRM know when a customer pays, so invoice status updates

              automatically without manual refresh.

            </InstructionCard>

            {isStripe && (
              <Alert severity="warning" sx={{ mb: 2, maxWidth: 720 }}>
                <MDTypography variant="button" fontWeight="bold" display="block" mb={0.5}>
                  Choose &quot;Your account&quot; — not &quot;Connected accounts&quot;
                </MDTypography>
                <MDTypography variant="body2">
                  When Stripe asks which events to listen to, select <strong>Your account</strong>.
                  PearlCRM connects <strong>your own</strong> Stripe account with your API keys.
                  Do <strong>not</strong> select Connected accounts — that is only for Stripe Connect
                  platforms, which PearlCRM does not use.
                </MDTypography>
              </Alert>
            )}

            <InstructionCard
              title={`How to add the webhook in ${meta.title}`}
              action={
                <MDButton
                  component="a"
                  href={meta.webhookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNew />}
                  sx={{ borderColor: meta.brandColor, color: meta.brandColor }}
                >
                  Open {meta.title} webhooks
                </MDButton>
              }
            >
              <NumberedSteps steps={meta.webhookSteps || []} brandColor={meta.brandColor} />
            </InstructionCard>

            <MDBox maxWidth={720}>

              <MDTypography variant="body2" color="text" mb={1.5}>

                Subscribe to: <strong>{meta.webhookEvents}</strong>

              </MDTypography>

              <TextField

                fullWidth
                label="Webhook URL — copy this into Stripe/Razorpay"
                value={webhookUrl}
                margin="normal"
                sx={{ bgcolor: "#fff" }}
                InputProps={{

                  readOnly: true,

                  endAdornment: (

                    <InputAdornment position="end">

                      <Tooltip title="Copy URL">

                        <IconButton onClick={copyWebhook} edge="end" size="small">

                          <ContentCopy />

                        </IconButton>

                      </Tooltip>

                    </InputAdornment>

                  ),

                }}
              />

              {webhookUrl &&
                /localhost|127\.0\.0\.1|\.local(?:host)?(?:[:/]|$)/i.test(webhookUrl) && (
                  <Alert severity="info" sx={{ mt: 1.5 }}>
                    <MDTypography variant="button" fontWeight="bold" display="block" mb={0.5}>
                      Local URL — Stripe/Razorpay cannot reach this from the internet
                    </MDTypography>
                    <MDTypography variant="body2">
                      Webhooks use <strong>POST</strong> (not GET). For local development, use the{" "}
                      <strong>Stripe CLI</strong> to forward events:{" "}
                      <code>stripe listen --forward-to {webhookUrl}</code>. In production, set{" "}
                      <strong>API_BASE_URL</strong> in backend env to your public HTTPS API (e.g.{" "}
                      <code>https://api.pearlcrm.com</code>) so this URL is reachable.
                    </MDTypography>
                  </Alert>
                )}

              <TextField

                fullWidth
                label="Webhook signing secret"
                type={showWebhookSecret ? "text" : "password"}
                value={webhookSecret}
                onChange={(e) => {
                  setWebhookEdited(true);
                  setWebhookSecret(e.target.value);
                }}
                onFocus={() => {
                  if (!webhookEdited && (webhookMasked || hasWebhookSecret)) {
                    setWebhookEdited(true);
                    setWebhookSecret("");
                    setShowWebhookSecret(true);
                  }
                }}
                placeholder={
                  webhookMasked || hasWebhookSecret
                    ? "Saved — type a new secret only to replace"
                    : isStripe
                      ? "whsec_…"
                      : "Webhook secret from Razorpay"
                }
                helperText={
                  !webhookEdited && (webhookMasked || hasWebhookSecret)
                    ? `Saved webhook secret on file${webhookMasked ? `: ${webhookMasked}` : ""}. Focus the field to replace it.`
                    : undefined
                }
                margin="normal"
                autoComplete="new-password"
                sx={{
                  bgcolor: "#fff",
                  "& .MuiInputBase-input": {
                    color: "#344767",
                    WebkitTextFillColor: "#344767",
                    opacity: 1,
                  },
                }}
                InputProps={{

                  endAdornment: (

                    <InputAdornment position="end">

                      <IconButton
                        onClick={() => setShowWebhookSecret((v) => !v)}
                        edge="end"
                        size="small"
                      >

                        {showWebhookSecret ? <VisibilityOff /> : <Visibility />}

                      </IconButton>

                    </InputAdornment>

                  ),

                }}
              />

              {!webhookReady && (

                <Alert severity="warning" sx={{ mt: 1 }}>

                  Webhook secret is recommended for automatic payment status updates.

                </Alert>

              )}

            </MDBox>

          </>

        );



      case 3:

        return (

          <>

            <MDTypography variant="h6" fontWeight="medium" mb={2}>

              Step 4 — Enable & save

            </MDTypography>

            <MDBox
              sx={{

                p: 2.5,

                borderRadius: 2,

                border: "1px solid",

                borderColor: "divider",

                bgcolor: "#fff",

                maxWidth: 720,

              }}
            >

              <FormControlLabel
                control={

                  <Switch
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    sx={{

                      "& .MuiSwitch-switchBase.Mui-checked": { color: meta.brandColor },

                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {

                        bgcolor: meta.brandColor,

                      },

                    }}
                  />

                }
                label={

                  <MDBox>

                    <MDTypography variant="body1" fontWeight="medium">

                      Enable {meta.title} for payments

                    </MDTypography>

                    <MDTypography variant="caption" color="text" display="block">

                      Staff can send payment links from invoices; customers can pay online via the

                      link you email them.

                    </MDTypography>

                  </MDBox>

                }
                sx={{ alignItems: "flex-start", ml: 0 }}
              />

            </MDBox>

            {isStripe && (
                <MDBox
                    sx={{ p: 2.5, mt: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "#fff", maxWidth: 720 }}
                >
                  <MDTypography variant="body1" fontWeight="medium">
                    Payment link expiry
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block" mb={1}>
                    How long a payment link stays valid after it is generated. Default: {expiryDefault} hours (
                    {formatHours(expiryDefault)}).
                  </MDTypography>

                  <MDBox display="flex" alignItems="flex-start" gap={1.5} flexWrap="wrap">
                    <TextField
                        label="Expiry (hours)"
                        value={expiryHours}
                        onChange={(e) => {
                          setExpiryEdited(true);
                          setExpiryReset(false);
                          setExpiryHours(e.target.value.replace(/[^\d]/g, ""));
                        }}
                        inputProps={{ inputMode: "numeric" }}
                        error={expiryEdited && !expiryReset && !expiryValid}
                        helperText={
                          expiryEdited && !expiryReset && !expiryValid
                              ? `Enter a whole number from ${EXPIRY_MIN_HOURS} to ${EXPIRY_MAX_HOURS} hours.`
                              : expiryValid
                                  ? `= ${formatHours(expiryNumber)}`
                                  : " "
                        }
                        sx={{ width: 220, bgcolor: "#fff" }}
                    />
                    {expiryNumber !== expiryDefault && !expiryReset && (
                        <MDButton
                            variant="text"
                            color="info"
                            size="small"
                            sx={{ mt: 1 }}
                            onClick={() => {
                              setExpiryHours(String(expiryDefault));
                              setExpiryEdited(true);
                              setExpiryReset(true);
                            }}
                        >
                          Reset to default
                        </MDButton>
                    )}
                  </MDBox>

                  <Alert severity="info" sx={{ mt: 1.5 }}>
                    Applies to newly generated payment links only. Links already sent keep their current
                    expiry. This setting applies to all payment gateways. Click <strong>Save connection</strong>{" "}
                    to apply.
                  </Alert>
                </MDBox>
            )}

            {!keysReady && (

              <Alert severity="error" sx={{ mt: 2, maxWidth: 720 }}>

                Complete step 2 and add API keys before enabling.

              </Alert>

            )}

            {keysReady && configured && (

              <Alert severity="success" sx={{ mt: 2, maxWidth: 720 }} icon={<CheckCircle />}>

                Keys are saved. Click <strong>Save connection</strong> below to apply changes.

              </Alert>

            )}

          </>

        );



      default:

        return null;

    }

  };



  return (

    <DashboardLayout>

      <DashboardNavbar />

      <MDBox pt={isV2 ? 0 : 4} pb={6} px={isV2 ? 0 : 2}>

        {/* Page header */}

        <MDBox display="flex" alignItems="flex-start" gap={2} mb={3} flexWrap="wrap">

          {/* v2: the breadcrumb and the settings menu lead back. */}
          {!isV2 && (
            <IconButton onClick={() => navigate("/settings/payments")} sx={{ mt: 0.5 }}>
              <ArrowBack />
            </IconButton>
          )}
          {meta.logo ? (
            <MDBox
              component="img"
              src={meta.logo}
              alt={meta.title}
              sx={{
                height: isStripe ? 36 : 52,
                width: "auto",
                maxWidth: isStripe ? 160 : 220,
                objectFit: "contain",
                mt: 0.5,
              }}
            />
          ) : null}
          <MDBox flex={1} minWidth={240}>

            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>

              <MDTypography variant="h4" fontWeight="medium" color="dark">

                Set up {meta.title}

              </MDTypography>

              {configured && enabled && (

                <Chip icon={<CheckCircle />} label="Live" color="success" size="small" />

              )}

              {configured && !enabled && (

                <Chip label="Configured · Disabled" color="warning" size="small" />

              )}

            </MDBox>

            <MDTypography variant="body2" color="text">

              {meta.description}

            </MDTypography>

          </MDBox>

        </MDBox>



        {error && (

          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>

            {typeof error === "string" ? error : JSON.stringify(error)}

          </Alert>

        )}

        {success && (

          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>

            {success}

          </Alert>

        )}



        {loading ? (

          <MDBox display="flex" justifyContent="center" py={8}>

            <CircularProgress sx={{ color: meta.brandColor }} />

          </MDBox>

        ) : (

          <Card
            sx={{

              p: { xs: 2, md: 3 },

              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",

              borderRadius: 2,

              bgcolor: "#fff",

            }}
          >

            <StepProgress
              steps={STEPS}
              activeStep={activeStep}
              brandColor={meta.brandColor}
              completedThrough={completedThrough}
              onStepClick={setActiveStep}
            />



            <MDBox minHeight={280}>{renderStepContent()}</MDBox>



            <Divider sx={{ my: 3 }} />



            <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>

              <MDButton
                color="secondary"
                onClick={activeStep === 0 ? () => navigate("/settings/payments") : goBack}
                startIcon={activeStep > 0 ? <ArrowBack /> : null}
              >

                {activeStep === 0 ? "Back to payments" : "Previous step"}

              </MDButton>



              <MDBox display="flex" gap={1} flexWrap="wrap">

                {activeStep < STEPS.length - 1 ? (

                  <MDButton
                    color="info"
                    endIcon={<ArrowForward />}
                    onClick={goNext}
                    disabled={activeStep === 1 && !keysReady}
                  >
                    Next step
                  </MDButton>

                ) : (

                  <MDButton
                    color="info"
                    onClick={handleSave}
                    disabled={saving || !keysReady || (isStripe && expiryEdited && !expiryReset && !expiryValid)}
                  >
                    {saving ? <CircularProgress size={20} color="inherit" /> : "Save connection"}
                  </MDButton>

                )}

              </MDBox>

            </MDBox>

          </Card>

        )}

      </MDBox>

      <Snackbar
        open={Boolean(success)}
        autoHideDuration={4000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccess("")} sx={{ width: "100%" }}>
          {success}
        </Alert>
      </Snackbar>

    </DashboardLayout>

  );

}

